import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  Snackbar,
  Alert,
  Chip,
  FormControlLabel,
  Divider,
  Pagination,
  Tooltip
} from '@mui/material';
import {
  Delete as DeleteIcon,
  ShoppingCart as ShoppingCartIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  WarningAmber as WarningAmberIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useCartStore, type CartItem } from '../stores/useCartStore';
import { useAppStore } from '../stores/useAppStore';
import { loanService } from '../services/loanService';
import { bookService } from '../services/bookService';
import { useEffect } from 'react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { vi } from 'date-fns/locale';

export const CartPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAppStore();
  const { items, removeItem, removeItems, clearCart, updateQuantity, updateDueDate, updateAllDueDates, syncBookData } = useCartStore();
  const [selectedItemIds, setSelectedItemIds] = useState<string[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItemsDialogOpen, setDeleteItemsDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedItemsForCheckout, setSelectedItemsForCheckout] = useState<typeof items>([]);
  const [defaultDueDate, setDefaultDueDate] = useState<Date | null>(() => {
    // Default: 10 days from today
    const date = new Date();
    date.setDate(date.getDate() + 10);
    return date;
  });
  const [bookDetailDialogOpen, setBookDetailDialogOpen] = useState(false);
  const [selectedBook, setSelectedBook] = useState<CartItem | null>(null);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);

  // Sync book data from server when cart loads (to get updated availableCopies)
  // Note: This only syncs the book data, NOT the quantity in cart
  // availableCopies is only updated when checkout is successful (in backend)
  useEffect(() => {
    const syncBooks = async () => {
      if (items.length === 0) return;

      try {
        // Fetch latest book data for each item in cart
        const syncPromises = items.map(async (item) => {
          try {
            const updatedBook = await bookService.getBookById(item.book.id);
            if (updatedBook) {
              syncBookData(updatedBook);
            }
          } catch (error: any) {
            // Book might have been deleted or doesn't exist (e.g., mock data IDs)
            // Silently ignore 404 errors - book data in cart will remain as is
            if (error?.message?.includes('404') || error?.message?.includes('not found')) {
              // Book doesn't exist - could be from old mock data, keep it in cart
              return;
            }
            // For other errors, log but don't remove from cart
            // console.warn will only show in development
          }
        });

        await Promise.all(syncPromises);
      } catch (error) {
        // Silently handle sync errors
      }
    };

    syncBooks();
  }, []); // Only run once on mount

  // Reset to page 1 when items change (e.g., after deletion)
  useEffect(() => {
    const totalPages = Math.ceil(items.length / pageSize);
    if (page > totalPages && totalPages > 0) {
      setPage(totalPages);
    } else if (totalPages === 0) {
      setPage(1);
    }
  }, [items.length, pageSize, page]);

  const handleSelectItem = (itemId: string) => {
    setSelectedItemIds(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  const handleSelectAll = () => {
    const startIndex = (page - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginatedItems = items.slice(startIndex, endIndex);
    const paginatedItemIds = paginatedItems.map(item => item.book.id);
    const allPaginatedSelected = paginatedItemIds.length > 0 && paginatedItemIds.every(id => selectedItemIds.includes(id));
    
    if (allPaginatedSelected) {
      // Deselect all paginated items
      setSelectedItemIds(prev => prev.filter(id => !paginatedItemIds.includes(id)));
    } else {
      // Select all paginated items
      setSelectedItemIds(prev => [...new Set([...prev, ...paginatedItemIds])]);
    }
  };

  const handleDeleteItem = (bookId: string) => {
    setSelectedItemIds([bookId]);
    setDeleteDialogOpen(true);
  };

  const handleDeleteSelected = () => {
    if (selectedItemIds.length === 0) {
      setSnackbarMessage('Vui lòng chọn ít nhất một cuốn sách để xóa');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }
    setDeleteItemsDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedItemIds.length === 1) {
      removeItem(selectedItemIds[0]);
      setSnackbarMessage('Đã xóa sách khỏi giỏ');
    } else {
      removeItems(selectedItemIds);
      setSnackbarMessage(`Đã xóa ${selectedItemIds.length} cuốn sách khỏi giỏ`);
    }
    const deletedIds = [...selectedItemIds];
    setSelectedItemIds([]);
    setDeleteDialogOpen(false);
    setDeleteItemsDialogOpen(false);
    setSnackbarSeverity('success');
    setSnackbarOpen(true);
    // Reset to page 1 if current page becomes empty
    setTimeout(() => {
      const remainingItemsAfterDelete = items.filter(item => !deletedIds.includes(item.book.id));
      const newTotalPagesAfterDelete = Math.ceil(remainingItemsAfterDelete.length / pageSize);
      if (page > newTotalPagesAfterDelete && newTotalPagesAfterDelete > 0) {
        setPage(newTotalPagesAfterDelete);
      } else if (newTotalPagesAfterDelete === 0) {
        setPage(1);
      }
    }, 0);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setDeleteItemsDialogOpen(false);
  };

  const handleCheckout = async (selectedItems?: CartItem[]) => {
    if (!user?.id) {
      setSnackbarMessage('Vui lòng đăng nhập để mượn sách');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    // Use provided selectedItems or items based on selectedItemIds
    const itemsToCheckout = selectedItems || items.filter(item => selectedItemIds.includes(item.book.id));

    if (itemsToCheckout.length === 0) {
      setSnackbarMessage('Vui lòng chọn ít nhất một cuốn sách để mượn');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    // Prepare book IDs (considering quantity)
    const bookIds: string[] = [];
    itemsToCheckout.forEach(item => {
      for (let i = 0; i < item.quantity; i++) {
        bookIds.push(item.book.id);
      }
    });

    // Store selected items in state for dialog display
    setSelectedItemsForCheckout(itemsToCheckout);
    setConfirmDialogOpen(true);
  };

  const handleQuickCheckout = async (item: CartItem) => {
    await handleCheckout([item]);
  };

  const handleConfirmCheckout = async () => {
    if (!user?.id) return;

    setIsProcessing(true);
    try {
      // Use selectedItemsForCheckout instead of all items
      const itemsToCheckout = selectedItemsForCheckout.length > 0 
        ? selectedItemsForCheckout 
        : items.filter(item => selectedItemIds.includes(item.book.id));

      if (itemsToCheckout.length === 0) {
        setSnackbarMessage('Vui lòng chọn ít nhất một cuốn sách để mượn');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
        setIsProcessing(false);
        return;
      }

      // Prepare book IDs and due dates (considering quantity)
      const bookIds: string[] = [];
      const dueDatesMap: { bookId: string; dueDate: string }[] = [];
      
      itemsToCheckout.forEach(item => {
        const dueDate = item.dueDate || (() => {
          // Fallback: default 10 days from today if not set
          const defaultDate = new Date();
          defaultDate.setDate(defaultDate.getDate() + 10);
          defaultDate.setHours(23, 59, 59, 999);
          return defaultDate.toISOString();
        })();
        
        for (let i = 0; i < item.quantity; i++) {
          bookIds.push(item.book.id);
          dueDatesMap.push({ bookId: item.book.id, dueDate });
        }
      });

      const result = await loanService.checkoutBooks(user.id, bookIds, dueDatesMap);
      
      if (result.success) {
        setSnackbarMessage(result.message);
        setSnackbarSeverity('success');
        setSnackbarOpen(true);
        
        // Remove checked out items from cart
        const checkedOutBookIds = itemsToCheckout.map(item => item.book.id);
        removeItems(checkedOutBookIds);
        
        setSelectedItemIds([]);
        setSelectedItemsForCheckout([]);
        setConfirmDialogOpen(false);
        
        setTimeout(() => {
          navigate('/dashboard');
        }, 2000);
      } else {
        setSnackbarMessage(result.message || 'Có lỗi xảy ra khi mượn sách');
        setSnackbarSeverity('error');
        setSnackbarOpen(true);
      }
    } catch (error: any) {
      setSnackbarMessage(error.message || 'Có lỗi xảy ra khi mượn sách');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleQuantityChange = (bookId: string, newQuantity: number) => {
    updateQuantity(bookId, newQuantity);
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '-';
    try {
      return new Date(dateString).toLocaleDateString('vi-VN', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      return '-';
    }
  };

  const getDueDate = (item: CartItem) => {
    if (item.dueDate) {
      return formatDate(item.dueDate);
    }
    // Fallback: 10 days from today
    const dueDate = new Date();
    dueDate.setDate(dueDate.getDate() + 10);
    return formatDate(dueDate.toISOString());
  };

  // Disable Saturday (6) and Sunday (0)
  const shouldDisableDate = (date: Date | null) => {
    if (!date) return false;
    const day = date.getDay();
    return day === 0 || day === 6; // 0 = Sunday, 6 = Saturday
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4" component="h1">
          Giỏ Sách
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {selectedItemIds.length > 0 && (
            <Tooltip title={`Xóa (${selectedItemIds.length})`} arrow>
              <IconButton
                color="error"
                onClick={handleDeleteSelected}
                aria-label="Xóa"
                size="small"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {items.length > 0 && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<CheckCircleIcon />}
              onClick={() => handleCheckout()}
              disabled={isProcessing || selectedItemIds.length === 0}
            >
              Xác nhận mượn sách ({selectedItemIds.length > 0 ? selectedItemIds.length : 'chọn sách'})
            </Button>
          )}
        </Box>
      </Box>

      {items.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <ShoppingCartIcon sx={{ fontSize: 80, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Giỏ sách trống
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Chưa có sách nào trong giỏ của bạn
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/books')}
          >
            Đi đến QL sách
          </Button>
        </Paper>
      ) : (
        <>
          {/* Default Due Date Selector */}
          <Paper sx={{ p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="body2" fontWeight="medium">
                Đặt ngày trả mặc định cho tất cả sách:
              </Typography>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={vi}>
                <DatePicker
                  label="Ngày trả mặc định"
                  value={defaultDueDate}
                  onChange={(newDate) => {
                    if (newDate) {
                      newDate.setHours(23, 59, 59, 999);
                      setDefaultDueDate(newDate);
                      updateAllDueDates(newDate.toISOString());
                    }
                  }}
                  minDate={new Date()}
                  shouldDisableDate={shouldDisableDate}
                  slotProps={{
                    textField: {
                      size: 'small',
                      sx: { width: '200px' }
                    }
                  }}
                />
              </LocalizationProvider>
              <Typography variant="caption" color="text.secondary">
                (Có thể thay đổi ngày trả cho từng sách riêng lẻ ở bảng dưới)
              </Typography>
            </Box>
          </Paper>
          
          <TableContainer component={Paper}>
          <Table sx={{ width: '100%', tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: '29%' }}>Tên Sách</TableCell>
                <TableCell sx={{ width: '16%' }}>Danh mục</TableCell>
                <TableCell align="center" sx={{ width: '10%' }}>Số lượng</TableCell>
                <TableCell align="center" sx={{ minWidth: '180px', width: '18%' }}>Ngày trả</TableCell>
                <TableCell align="center" sx={{ width: '15%' }}>Thao tác</TableCell>
                <TableCell padding="none" align="center" sx={{ width: '14px', minWidth: '14px', maxWidth: '14px', padding: '0px !important' }}>
                  {(() => {
                    const startIndex = (page - 1) * pageSize;
                    const endIndex = startIndex + pageSize;
                    const paginatedItems = items.slice(startIndex, endIndex);
                    const paginatedItemIds = paginatedItems.map(item => item.book.id);
                    const allPaginatedSelected = paginatedItemIds.length > 0 && paginatedItemIds.every(id => selectedItemIds.includes(id));
                    const somePaginatedSelected = paginatedItemIds.some(id => selectedItemIds.includes(id));
                    
                    return (
                      <Checkbox
                        indeterminate={somePaginatedSelected && !allPaginatedSelected}
                        checked={allPaginatedSelected}
                        onChange={handleSelectAll}
                        size="small"
                        sx={{ padding: '0px', '& svg': { fontSize: '20px' } }}
                      />
                    );
                  })()}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(() => {
                const startIndex = (page - 1) * pageSize;
                const endIndex = startIndex + pageSize;
                const paginatedItems = items.slice(startIndex, endIndex);
                
                if (paginatedItems.length === 0) {
                  return (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
                          <Typography variant="h6" color="text.secondary">
                            Giỏ hàng trống
                          </Typography>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                }
                
                return paginatedItems.map((item) => (
                  <TableRow key={item.book.id}>
                    <TableCell>
                      <Typography 
                        variant="body2" 
                        fontWeight="medium"
                        onClick={() => {
                          setSelectedBook(item);
                          setBookDetailDialogOpen(true);
                        }}
                        sx={{
                          maxWidth: '100%',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          display: 'block',
                          cursor: 'pointer',
                          '&:hover': {
                            textDecoration: 'underline',
                            color: 'primary.main'
                          }
                        }}
                      >
                        {item.book.title.length > 40 ? (
                          <Tooltip title={item.book.title} arrow>
                            <span>{item.book.title.substring(0, 40)}...</span>
                          </Tooltip>
                        ) : (
                          item.book.title
                        )}
                      </Typography>
                      {item.book.author && (
                        <Typography variant="caption" color="text.secondary">
                          {item.book.author}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        {item.book.category ? (
                          <Chip label={item.book.category.name} size="small" />
                        ) : (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Tooltip title="Danh mục không khớp dữ liệu hiện có">
                              <WarningAmberIcon color="warning" fontSize="small" />
                            </Tooltip>
                            <Typography variant="caption" color="text.secondary">
                              Chưa phân loại
                            </Typography>
                          </Box>
                        )}
                        <Chip
                          label={item.book.availableCopies > 0 ? 'Có sẵn' : 'Hết sách'}
                          color={item.book.availableCopies > 0 ? 'success' : 'error'}
                          size="small"
                          sx={{ width: 'fit-content' }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <TextField
                        type="number"
                        value={item.quantity}
                        onChange={(e) => {
                          const newQuantity = parseInt(e.target.value) || 1;
                          if (newQuantity >= 1 && newQuantity <= item.book.availableCopies) {
                            handleQuantityChange(item.book.id, newQuantity);
                          }
                        }}
                        inputProps={{
                          min: 1,
                          max: item.book.availableCopies,
                          style: { textAlign: 'center', width: '60px' }
                        }}
                        size="small"
                        sx={{ width: '80px' }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        Có sẵn: {item.book.availableCopies}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={vi}>
                        <DatePicker
                          label="Ngày trả"
                          value={item.dueDate ? new Date(item.dueDate) : null}
                          onChange={(newDate) => {
                            if (newDate) {
                              newDate.setHours(23, 59, 59, 999); // End of day
                              updateDueDate(item.book.id, newDate.toISOString());
                            }
                          }}
                          minDate={new Date()} // Không cho chọn ngày trong quá khứ
                          shouldDisableDate={shouldDisableDate} // Chặn thứ 7 và Chủ Nhật
                          slotProps={{
                            textField: {
                              size: 'small',
                              sx: { width: '160px' }
                            }
                          }}
                        />
                      </LocalizationProvider>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
                        <Tooltip title="Mượn sách">
                          <span>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleQuickCheckout(item)}
                              disabled={item.book.availableCopies <= 0 || isProcessing}
                            >
                              <CheckCircleIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Xóa khỏi giỏ">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDeleteItem(item.book.id)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell padding="none" align="center" sx={{ width: '14px', minWidth: '14px', maxWidth: '14px', padding: '0px !important' }}>
                      <Checkbox
                        checked={selectedItemIds.includes(item.book.id)}
                        onChange={() => handleSelectItem(item.book.id)}
                        size="small"
                        sx={{ padding: '0px', '& svg': { fontSize: '20px' } }}
                      />
                    </TableCell>
                  </TableRow>
                ));
              })()}
            </TableBody>
          </Table>
        </TableContainer>
        
        {items.length > pageSize && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3, mb: 2 }}>
            <Pagination
              count={Math.ceil(items.length / pageSize)}
              page={page}
              onChange={(_, value) => setPage(value)}
              color="primary"
              showFirstButton
              showLastButton
            />
          </Box>
        )}
        </>
      )}

      {/* Delete Single Item Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Xác nhận xóa</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Bạn có chắc chắn muốn xóa cuốn sách này khỏi giỏ không? Sách sẽ được trả lại tổng số lượng đã có.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Hủy</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Xóa
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Multiple Items Dialog */}
      <Dialog open={deleteItemsDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Xác nhận xóa</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Bạn có chắc chắn muốn xóa {selectedItemIds.length} cuốn sách này khỏi giỏ không? Sách sẽ được trả lại tổng số lượng đã có.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Hủy</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Xóa
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm Checkout Dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => !isProcessing && setConfirmDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Xác nhận mượn sách</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Vui lòng xác nhận thông tin mượn sách sau:
          </DialogContentText>
          
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Danh sách sách mượn ({selectedItemsForCheckout.length > 0 
                ? selectedItemsForCheckout.reduce((sum, item) => sum + item.quantity, 0)
                : selectedItemIds.length > 0
                ? items.filter(item => selectedItemIds.includes(item.book.id))
                    .reduce((sum, item) => sum + item.quantity, 0)
                : 0} cuốn):
            </Typography>
            <Box component="ul" sx={{ pl: 3, mb: 2 }}>
              {(selectedItemsForCheckout.length > 0 
                ? selectedItemsForCheckout 
                : items.filter(item => selectedItemIds.includes(item.book.id))
              ).map((item) => (
                <li key={item.book.id}>
                  <Typography variant="body2">
                    {item.book.title} - {item.book.author}
                    {item.quantity > 1 && ` (${item.quantity} cuốn)`}
                    {' - '}
                    <strong>Hạn trả:</strong> {getDueDate(item)}
                  </Typography>
                </li>
              ))}
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box>
            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
              Thông tin mượn:
            </Typography>
            <Box sx={{ pl: 2 }}>
              <Typography variant="body2" gutterBottom>
                <strong>Người mượn:</strong> {user?.fullName}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Thời gian mượn:</strong> {formatDate(new Date().toISOString())}
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Hạn trả (mặc định):</strong> {selectedItemsForCheckout.length > 0 && selectedItemsForCheckout[0]?.dueDate 
                  ? formatDate(selectedItemsForCheckout[0].dueDate)
                  : (() => {
                      const defaultDate = new Date();
                      defaultDate.setDate(defaultDate.getDate() + 10);
                      return formatDate(defaultDate.toISOString());
                    })()}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                (Mỗi sách có thể có ngày trả khác nhau, xem chi tiết ở danh sách trên)
              </Typography>
              <Typography variant="body2" gutterBottom>
                <strong>Tổng số sách:</strong> {selectedItemsForCheckout.length > 0 
                  ? selectedItemsForCheckout.reduce((sum, item) => sum + item.quantity, 0)
                  : selectedItemIds.length > 0
                  ? items.filter(item => selectedItemIds.includes(item.book.id))
                      .reduce((sum, item) => sum + item.quantity, 0)
                  : 0} cuốn
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setConfirmDialogOpen(false)} 
            disabled={isProcessing}
            startIcon={<CancelIcon />}
          >
            Hủy
          </Button>
          <Button 
            onClick={handleConfirmCheckout} 
            variant="contained" 
            color="primary"
            disabled={isProcessing}
            startIcon={<CheckCircleIcon />}
          >
            {isProcessing ? 'Đang xử lý...' : 'Xác nhận mượn'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Book Detail Dialog */}
      <Dialog open={bookDetailDialogOpen} onClose={() => setBookDetailDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h5" component="div">
            {selectedBook?.book.title}
          </Typography>
          {selectedBook?.book.author && (
            <Typography variant="subtitle1" color="text.secondary" component="div">
              Tác giả: {selectedBook.book.author}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          {selectedBook && (
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: 3, mt: 1 }}>
              <Box>
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Thông tin sách
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Tên sách
                      </Typography>
                      <Typography variant="body1">{selectedBook.book.title || 'N/A'}</Typography>
                    </Box>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">
                        Tác giả
                      </Typography>
                      <Typography variant="body1">{selectedBook.book.author || 'N/A'}</Typography>
                    </Box>
                    {selectedBook.book.isbn && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          ISBN
                        </Typography>
                        <Typography variant="body1">{selectedBook.book.isbn}</Typography>
                      </Box>
                    )}
                    {selectedBook.book.publisher && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Nhà xuất bản
                        </Typography>
                        <Typography variant="body1">{selectedBook.book.publisher}</Typography>
                      </Box>
                    )}
                    {selectedBook.book.publishYear && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Năm xuất bản
                        </Typography>
                        <Typography variant="body1">{selectedBook.book.publishYear}</Typography>
                      </Box>
                    )}
                    {selectedBook.book.category && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          Danh mục
                        </Typography>
                        <Chip label={selectedBook.book.category.name} size="small" />
                      </Box>
                    )}
                  </Box>
                </Paper>
                {selectedBook.book.subjects && selectedBook.book.subjects.length > 0 && (
                  <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Chủ đề
                    </Typography>
                    <Box display="flex" flexWrap="wrap" gap={1}>
                      {selectedBook.book.subjects.map((subject, index) => (
                        <Chip key={index} label={subject} size="small" />
                      ))}
                    </Box>
                  </Paper>
                )}
                {selectedBook.book.summary && (
                  <Paper sx={{ p: 2 }}>
                    <Typography variant="h6" gutterBottom>
                      Tóm tắt
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                      {selectedBook.book.summary}
                    </Typography>
                  </Paper>
                )}
              </Box>
              <Box>
                <Paper sx={{ p: 2, mb: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Trạng thái
                  </Typography>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Số lượng
                    </Typography>
                    <Typography variant="body1">
                      {selectedBook.book.availableCopies}/{selectedBook.book.totalCopies} có sẵn
                    </Typography>
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      Trạng thái
                    </Typography>
                    <Chip
                      label={selectedBook.book.availableCopies > 0 ? 'Có sẵn' : 'Hết sách'}
                      color={selectedBook.book.availableCopies > 0 ? 'success' : 'error'}
                      size="small"
                    />
                  </Box>
                  {selectedBook.book.availableCopies <= 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Alert severity="warning">
                        Sách này hiện không còn sẵn có. Không thể mượn.
                      </Alert>
                    </Box>
                  )}
                </Paper>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="h6" gutterBottom>
                    Thông tin mượn
                  </Typography>
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary">
                      Số lượng muốn mượn
                    </Typography>
                    <Typography variant="body1">{selectedBook.quantity}</Typography>
                  </Box>
                  {selectedBook.dueDate && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="subtitle2" color="text.secondary">
                        Ngày trả dự kiến
                      </Typography>
                      <Typography variant="body2">
                        {new Date(selectedBook.dueDate).toLocaleDateString('vi-VN')}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBookDetailDialogOpen(false)}>Đóng</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Container>
  );
};
